import { generateUuid } from '../../../../utils/uuids'
import { DateTermV2 } from '../../../../domain/value-object/DateTermV2'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { createDelta } from '../../../../domain/value-object/ItemDeltaInputVO'
import {
  ReferencedEntity,
  ReferencedEntityWithIcon,
} from '../../../../domain/value-object/ReferencedEntity'
import {
  DEFAULT_PROJECT_STATUS,
  DEFAULT_WBS_ITEM_CODE_FORMAT,
  ProjectStatus,
  WbsItemCodeFormat,
} from '../../../../domain/entity/ProjectEntity'
import {
  ProjectCreateProps,
  ProjectDeleteProps,
  ProjectDetail,
  ProjectUpdateDeltaProps,
} from '../../../../infrastructure/api/project'

export type ProjectFormModel = {
  uuid: string
  lockVersion: number
  code: string
  iconUrl: string
  displayName: string
  officialName: string
  businessClient: ReferencedEntity | undefined
  division: ReferencedEntity | undefined
  projectManager: ReferencedEntityWithIcon | undefined
  status: ProjectStatus
  scheduledDate: DateTermV2
  wbsItemCodeFormat: WbsItemCodeFormat
  mainEmail: string
  // GitHubレビュアー同期。情報画面(projectInformation)のみで表示・編集する項目。
  githubReviewerSyncEnabled: boolean
}

export const createInitialValue = (): ProjectFormModel => ({
  uuid: generateUuid(),
  lockVersion: 0,
  code: '',
  iconUrl: '',
  displayName: '',
  officialName: '',
  businessClient: undefined,
  division: undefined,
  projectManager: undefined,
  // 作成時のステータスはサーバー側で STANDBY 固定（初期値）。
  status: DEFAULT_PROJECT_STATUS,
  scheduledDate: { startDate: undefined, endDate: undefined },
  wbsItemCodeFormat: DEFAULT_WBS_ITEM_CODE_FORMAT,
  mainEmail: '',
  githubReviewerSyncEnabled: false,
})

export const fromDetail = (detail: ProjectDetail): ProjectFormModel => ({
  uuid: detail.uuid,
  lockVersion: detail.lockVersion,
  code: detail.code,
  iconUrl: detail.iconUrl || '',
  displayName: detail.displayName,
  officialName: detail.officialName,
  businessClient: detail.businessClient
    ? {
        uuid: detail.businessClient.uuid,
        name: detail.businessClient.displayName,
      }
    : undefined,
  division: detail.division
    ? { uuid: detail.division.uuid, name: detail.division.displayName }
    : undefined,
  projectManager: detail.projectManager
    ? {
        uuid: detail.projectManager.uuid,
        name: detail.projectManager.name,
        iconUrl: detail.projectManager.iconUrl,
      }
    : undefined,
  status: detail.status,
  scheduledDate: {
    startDate: dateV2Service.fromApiResponseToVo(
      detail.scheduledDate?.startDate
    ),
    endDate: dateV2Service.fromApiResponseToVo(detail.scheduledDate?.endDate),
  },
  wbsItemCodeFormat: detail.wbsItemCodeFormat,
  mainEmail: detail.mainEmail || '',
  githubReviewerSyncEnabled: detail.githubReviewerSyncEnabled ?? false,
})

export const toCreateInput = (
  formModel: ProjectFormModel
): ProjectCreateProps => ({
  // UI で採番済みの uuid をそのまま採用させる（サーバーは uuid 未指定時のみ採番）。
  uuid: formModel.uuid,
  code: formModel.code,
  displayName: formModel.displayName,
  officialName: formModel.officialName,
  iconUrl: formModel.iconUrl,
  divisionUuid: formModel.division?.uuid || '',
  businessClientUuid: formModel.businessClient?.uuid || '',
  projectManagerUuid: formModel.projectManager?.uuid || '',
  scheduledDate: {
    startDate: toApiDate(formModel.scheduledDate.startDate),
    endDate: toApiDate(formModel.scheduledDate.endDate),
  },
  mainEmail: formModel.mainEmail,
  wbsItemCodeFormat: formModel.wbsItemCodeFormat,
})

// 実際に変更された項目だけを含む差分更新ペイロードを組み立てる。
// "code" と "wbsItemCodeFormat" は更新不可のため含めない。
export const toDeltaInput = (
  entity: ProjectDetail,
  formModel: ProjectFormModel
): ProjectUpdateDeltaProps => {
  const before = fromDetail(entity)
  return {
    uuid: formModel.uuid,
    displayName: createDelta(before.displayName, formModel.displayName),
    officialName: createDelta(before.officialName, formModel.officialName),
    iconUrl: createDelta(before.iconUrl, formModel.iconUrl),
    divisionUuid: createDelta(before.division?.uuid, formModel.division?.uuid),
    businessClientUuid: createDelta(
      before.businessClient?.uuid,
      formModel.businessClient?.uuid
    ),
    projectManagerUuid: createDelta(
      before.projectManager?.uuid,
      formModel.projectManager?.uuid
    ),
    status: createDelta(before.status, formModel.status),
    scheduledDate: createScheduledDateDelta(
      before.scheduledDate,
      formModel.scheduledDate
    ),
    mainEmail: createDelta(before.mainEmail, formModel.mainEmail),
    githubReviewerSyncEnabled: createDelta(
      before.githubReviewerSyncEnabled,
      formModel.githubReviewerSyncEnabled
    ),
  }
}

export const toDeleteInput = (
  formModel: ProjectFormModel
): ProjectDeleteProps => ({
  uuid: formModel.uuid,
  lockVersion: formModel.lockVersion,
  displayName: formModel.displayName,
  officialName: formModel.officialName,
  iconUrl: formModel.iconUrl,
  divisionUuid: formModel.division?.uuid || '',
  businessClientUuid: formModel.businessClient?.uuid || '',
  projectManagerUuid: formModel.projectManager?.uuid || '',
  scheduledDate: {
    startDate: toApiDate(formModel.scheduledDate.startDate),
    endDate: toApiDate(formModel.scheduledDate.endDate),
  },
  mainEmail: formModel.mainEmail,
})

const toApiDate = (date: DateTermV2['startDate']): string =>
  date ? dateV2Service.fromVoToApiRequest(date) : ''

const createScheduledDateDelta = (before: DateTermV2, after: DateTermV2) => {
  const oldValue = {
    startDate: before.startDate ? toApiDate(before.startDate) : undefined,
    endDate: before.endDate ? toApiDate(before.endDate) : undefined,
  }
  const newValue = {
    startDate: after.startDate ? toApiDate(after.startDate) : undefined,
    endDate: after.endDate ? toApiDate(after.endDate) : undefined,
  }
  if (
    oldValue.startDate === newValue.startDate &&
    oldValue.endDate === newValue.endDate
  ) {
    return undefined
  }
  return { oldValue, newValue }
}
